import type { ApiKeySummary, OrgMemberSummary } from '@nadoutong/contracts';
import { useState } from 'react';
import { useRevokeApiKey } from '../../api/useKeys.ts';
import { apiKeyDisplayName, apiKeyRevokePhrase } from '../../components/apiKeyName.ts';
import { Modal } from '../../components/Modal.tsx';
import { localizeCaughtError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { formatDate } from './formatDate.ts';

/**
 * The API keys assigned to one member — opened from the member
 * directory's keys cell (epic #1 PR-4, from console-web's `/team`).
 *
 * Listing is readable with `org:api_keys:read`; revoking is offered
 * only when the session also carries `org:api_keys:manage`
 * (`canRevoke`, decided by `Organization.tsx` off the session — same
 * gate console-web's member dialog uses). This used to be read-only on
 * purpose, which left the console with NO admin path to pull a
 * member's leaked credential: `/keys` lists only the caller's own
 * keys, so an org manager could see a member's key here but had
 * nowhere to revoke it. Revoke therefore lives here now, on the same
 * `useRevokeApiKey` hook as `/keys` (one DELETE, one `['keys']`
 * invalidation — this dialog's list and the directory's key counts
 * both derive from that query, so both refresh on success).
 *
 * The confirm is a danger `ConfirmDialog` that requires retyping the
 * key's name (prefix for unnamed keys — `apiKeyRevokePhrase`, the same
 * rule as the `/keys` revoke modal). Exactly one modal is mounted at a
 * time: while the confirm is up it replaces the list dialog, and
 * cancelling returns to it. A rejected revoke reports inline in the
 * confirm and keeps it open — never a silent no-op.
 */

interface Props {
  readonly member: OrgMemberSummary;
  readonly keys: readonly ApiKeySummary[];
  readonly canRevoke: boolean;
  readonly onClose: () => void;
}

export function MemberKeysDialog({ member, keys, canRevoke, onClose }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const revokeM = useRevokeApiKey();
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const memberName = member.displayName ?? member.email;
  const titleId = 'organization-member-keys-title';
  const descriptionId = 'organization-member-keys-description';

  async function handleRevoke(): Promise<void> {
    if (revokeTarget === null) return;
    setRevokeError(null);
    try {
      await revokeM.mutateAsync(revokeTarget.id);
      setRevokeTarget(null);
      toast.success(t('organization.members.keys.toast.revoked'));
    } catch (err) {
      setRevokeError(localizeCaughtError(err, t, 'organization.members.keys.revoke_error'));
    }
  }

  if (revokeTarget !== null) {
    return (
      <ConfirmDialog
        open
        onOpenChange={(open) => {
          if (!open && !revokeM.isPending) {
            setRevokeTarget(null);
            setRevokeError(null);
          }
        }}
        tone="danger"
        title={t('organization.members.keys.revoke_title')}
        body={
          <div className="flex flex-col gap-2">
            <p className="m-0">{t('organization.members.keys.revoke_body')}</p>
            <p className="m-0 font-mono text-caption text-ink-mid">
              {apiKeyDisplayName(revokeTarget.name, revokeTarget.prefix, t)} · {revokeTarget.prefix}
            </p>
            {revokeError !== null ? (
              <p
                role="alert"
                data-testid="organization-member-key-revoke-error"
                className="m-0 text-caption text-bad"
              >
                {revokeError}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={t('organization.members.keys.revoke_confirm')}
        cancelLabel={t('organization.action.cancel')}
        closeLabel={t('organization.members.keys.close')}
        typeToConfirm={apiKeyRevokePhrase(revokeTarget.name, revokeTarget.prefix)}
        typeToConfirmLabel={t('organization.members.keys.revoke_type_label')}
        loading={revokeM.isPending}
        testId="organization-member-key-revoke-dialog"
        onConfirm={() => {
          void handleRevoke();
        }}
      />
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId={titleId}
      descriptionId={descriptionId}
      testId="organization-member-keys-dialog"
    >
      <div className="flex flex-col gap-4 p-5">
        <header className="flex flex-col gap-1">
          <h3 id={titleId} className="m-0 text-heading text-ink">
            {t('organization.members.keys.dialog_title', { member: memberName })}
          </h3>
          <p id={descriptionId} className="m-0 text-caption text-ink-low">
            {t('organization.members.keys.dialog_subtitle')}
          </p>
        </header>

        {keys.length === 0 ? (
          <EmptyState
            testId="organization-member-keys-empty"
            title={t('organization.members.keys.empty')}
          />
        ) : (
          <Table testId="organization-member-keys-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('organization.members.keys.column.name')}</Th>
                <Th>{t('organization.members.keys.column.prefix')}</Th>
                <Th>{t('organization.members.keys.column.created')}</Th>
                <Th>{t('organization.members.keys.column.last_used')}</Th>
                {canRevoke ? <Th>{t('organization.members.keys.column.actions')}</Th> : null}
              </Tr>
            </THead>
            <TBody>
              {keys.map((key) => (
                <Tr key={key.id} data-testid={`organization-member-key-${key.id}`}>
                  <Td heading>{apiKeyDisplayName(key.name, key.prefix, t)}</Td>
                  <Td mono label={t('organization.members.keys.column.prefix')}>
                    {key.prefix}
                  </Td>
                  <Td mono label={t('organization.members.keys.column.created')}>
                    {formatDate(key.createdAt)}
                  </Td>
                  <Td mono label={t('organization.members.keys.column.last_used')}>
                    {key.lastUsedAt === null
                      ? t('organization.members.keys.never_used')
                      : formatDate(key.lastUsedAt)}
                  </Td>
                  {canRevoke ? (
                    <Td label={t('organization.members.keys.column.actions')} stack>
                      <div className="flex items-center justify-end max-md:justify-start">
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={revokeM.isPending}
                          onClick={() => {
                            setRevokeError(null);
                            setRevokeTarget(key);
                          }}
                          data-testid={`organization-member-key-revoke-${key.id}`}
                        >
                          {t('organization.members.keys.revoke')}
                        </Button>
                      </div>
                    </Td>
                  ) : null}
                </Tr>
              ))}
            </TBody>
          </Table>
        )}

        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={onClose}
            data-testid="organization-member-keys-close"
          >
            {t('organization.members.keys.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
